import React, { useEffect, useState, FC } from 'react';
import { useParams } from 'next/navigation';
import { TrashIcon } from '@heroicons/react/24/outline';
import { TStoreInventory } from 'shared/types/cart';
import { LineItem } from 'shared/types/wishlist/LineItem';
import { Wishlist as WishlistType } from 'shared/types/wishlist/Wishlist';
import Button from 'components/commercetools-ui/atoms/button';
import ItemDiscontinued from 'components/commercetools-ui/atoms/item-discontinued';
import ItemRestricted from 'components/commercetools-ui/atoms/item-restricted';
import StockMessage from 'components/commercetools-ui/atoms/stock-message';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import { useCart, useWishlist, useAccount } from 'frontastic';
import Image, { ImageProps } from 'frontastic/lib/image';

export type TWishlistItem = LineItem & { isOnStock?: boolean; fluentAvailableQuantity?: number };

export interface Props {
  item: TWishlistItem;
  isPage?: boolean; // this indicates whether this component rendered in a page or modal
  isShared?: boolean | null;
  productLogo?: ImageProps;
  fluentStocks?: TStoreInventory[];
}

interface ItemContainerProps {
  children?: React.ReactNode;
}

const WishlistItem: FC<Props> = ({ item, isShared, fluentStocks, isPage = false, productLogo }) => {
  const { getCurrency, ctLocale: locale } = useLocale();
  const { formatMessage: formatWishlistMessage } = useFormat({ name: 'wishlist' });
  const [isRestricted, setISRestricted] = useState(false);
  const { data: wishlist, removeLineItem } = useWishlist();
  const { account, loggedIn } = useAccount();
  const { addItem } = useCart();

  useEffect(() => {
    const countryMapper: Record<string, string> = {
      GBP: 'UK',
      EUR: 'EU',
    };
    const isItemRestricted = item?.variant?.attributes?.exclusion?.some(
      (exclusion: any) => exclusion.label === countryMapper[getCurrency()],
    );

    if (isItemRestricted) {
      setISRestricted(true);
    } else {
      setISRestricted(false);
    }
  }, [item, getCurrency]);

  const currentUrl = document.location.origin;
  const moveToCart = () => {
    if (wishlist) removeLineItem(wishlist, item);
    if (item.variant) addItem({ ...item.variant, queryID: item.queryID }, 1);
    const salutation = loggedIn ? account?.salutation || '' : '';
    const dataLayer = new DataLayerHelpers(currentUrl, locale, item, account?.email, account?.accountId, salutation);
    dataLayer.setAddToCartDataLayer();
  };

  const ItemContainer = ({ children }: ItemContainerProps) => {
    return (
      <div
        className={`flex items-stretch justify-start gap-10 py-18 md:gap-15 ${
          isPage ? 'max-w-full' : 'h-[145px] w-[125px]'
        }`}
      >
        {children}
      </div>
    );
  };

  const renderAdditionalInfo = () => {
    const { optionsOrder, ...attributes } = item?.variant?.attributes || {};
    if (!optionsOrder || optionsOrder.length === 0) {
      return null;
    }

    return optionsOrder.map((optionKey: string) => {
      const value = attributes[optionKey];
      if (!value) return null;
      return (
        <div key={optionKey} className="mt-4">
          <span className="text-14 font-light capitalize text-secondary-grey">{optionKey.replace(/_/g, ' ')}:</span>{' '}
          <span className="text-14 font-light capitalize text-secondary-grey">
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        </div>
      );
    });
  };

  const PriceDisplay: FC = () => (
    <div className="mt-8">
      {item.variant?.discountedPrice ? (
        <div className="flex items-center gap-5">
          <span className="text-20 font-medium leading-loose text-accent-red">
            {CurrencyHelpers.formatForCurrency(item.variant.discountedPrice, locale)}
          </span>
          <span className="text-14 font-normal leading-loose text-gray-500 line-through">
            {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
          </span>
        </div>
      ) : (
        <span className="text-20 font-medium leading-loose">
          {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
        </span>
      )}
    </div>
  );

  const ItemStatus = () => {
    if (item?.variant?.attributes?.isDiscontinued) {
      return (
        <div className="mb-4 mt-8">
          <ItemDiscontinued />
        </div>
      );
    }

    if (isRestricted) {
      return (
        <div className="mb-4 mt-8">
          <ItemRestricted />
        </div>
      );
    }

    return (
      <StockMessage
        isOnStock={item.isOnStock}
        count={item.count}
        fluentAvailableQuantity={item.fluentAvailableQuantity}
      />
    );
  };

  const RemoveButton = (classNames: string) => (
    <span onClick={() => removeLineItem(wishlist as WishlistType, item)} className={classNames}>
      Remove
    </span>
  );

  return (
    <>
      {isPage ? (
        <ItemContainer>
          <div className="w-[125px] shrink-0 bg-white p-12">
            <a href={item._url ?? '#'}>
              <div className="relative h-full w-full rounded-sm">
                <Image
                  src={item.variant?.images?.[0] || productLogo?.media?.file}
                  suffix="small"
                  style={{ objectFit: 'contain' }}
                  fill
                  alt={item.name}
                />
              </div>
            </a>
          </div>
          <div className="grow overflow-hidden">
            <div className="flex items-center justify-between">
              <a href={item._url ?? '#'} className="w-[80%]">
                <p
                  className="truncate whitespace-nowrap text-14 leading-loose text-link-normal transition-colors hover:text-link-hover"
                  title={item.name}
                >
                  {item.name}
                </p>
              </a>
              {isShared == false ? RemoveButton('hidden cursor-pointer text-13 font-light underline md:block') : null}
            </div>

            {fluentStocks && fluentStocks.length !== 0 && ItemStatus()}

            <PriceDisplay />

            {renderAdditionalInfo()}

            <div className="mt-16 flex w-fit flex-col gap-16 text-12">
              {isShared === false ? (
                <Button
                  variant="primary"
                  onClick={moveToCart}
                  disabled={
                    ((item?.isOnStock || item?.variant?.attributes?.isPerpetualStock) &&
                      item.variant?.attributes?.isDiscontinued) || // has stock (or perpetual stock) and is discontinued
                    !(item?.isOnStock || item?.variant?.attributes?.isPerpetualStock) || // has no stock and not perpetual stock
                    isRestricted // is restricted
                  }
                  className="py-8 text-14"
                >
                  {formatWishlistMessage({ id: 'wishlist.add.to.cart', defaultMessage: 'Add to cart' })}
                </Button>
              ) : null}
              {/* mobile only */}
              {isShared == false ? RemoveButton('cursor-pointer text-13 font-light underline md:hidden') : null}
            </div>
          </div>
        </ItemContainer>
      ) : (
        <div className="flex max-w-full items-stretch justify-start gap-10 py-18 md:gap-15">
          <div className="h-[145px] w-[125px] shrink-0 bg-white p-12">
            <div className="relative h-full w-full">
              <Image
                src={item.variant?.images?.[0] || productLogo?.media?.file}
                suffix="small"
                style={{ objectFit: 'contain' }}
                alt={item.name ?? ''}
                fill
              />
            </div>
          </div>
          <div className="grow">
            <div className="flex max-w-full items-center justify-between">
              <p className="max-w-[150px] overflow-hidden text-ellipsis whitespace-pre text-14 leading-loose">
                {item.name}
              </p>
              <i onClick={() => removeLineItem(wishlist as WishlistType, item)} className="block cursor-pointer">
                <TrashIcon stroke="#494949" className="w-20" />
              </i>
            </div>
            <div className="mt-12">
              {item.variant?.discountedPrice ? (
                <div className="flex items-center gap-5">
                  <span className="text-14 font-medium leading-loose text-accent-red">
                    {CurrencyHelpers.formatForCurrency(item.variant.discountedPrice, locale)}
                  </span>
                  <span className="text-12 font-normal leading-loose text-gray-500 line-through">
                    {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
                  </span>
                </div>
              ) : (
                <span className="text-14 font-medium leading-loose">
                  {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
                </span>
              )}
            </div>

            {fluentStocks && fluentStocks.length !== 0 && ItemStatus()}
            <div className="mt-16 leading-normal">
              <Button
                variant="primary"
                onClick={moveToCart}
                disabled={
                  ((item?.isOnStock || item?.variant?.attributes?.isPerpetualStock) &&
                    item.variant?.attributes?.isDiscontinued) || // has stock (or perpetual stock) and is discontinued
                  !(item?.isOnStock || item?.variant?.attributes?.isPerpetualStock) || // has no stock and not perpetual stock
                  isRestricted // is restricted
                }
                className="py-8 text-14"
              >
                {formatWishlistMessage({ id: 'wishlist.add.to.cart', defaultMessage: 'Add to cart' })}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WishlistItem;
